import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ArrowRight, ChevronDown, MessageSquare, PhoneCall, ShieldCheck, Bot, Sun, Moon } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, TOOL_LINKS, PRODUCT_LINKS, PLATFORM_LINKS, SOLUTION_LINKS } from "../../data/content";
import { useTheme } from "../../theme/ThemeContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "../ui/sheet";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const productIcons = { "/vor": MessageSquare, "/logberg": PhoneCall, "/skjold": ShieldCheck, "/defence": Bot };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" aria-label="norse3 home" className="shrink-0 inline-flex items-center h-16">
          <Logo size={34} />
        </Link>

        <div className="hidden lg:flex items-center gap-0.5">
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 px-2.5 py-2 text-[0.82rem] whitespace-nowrap rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 focus:outline-none" data-testid="nav-platform-trigger">
              Platform <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-navy-800 border-white/12 w-72 p-1.5">
              {PLATFORM_LINKS.map((t) => (
                <DropdownMenuItem key={t.to} asChild className="rounded-lg focus:bg-white/5 cursor-pointer">
                  <Link to={t.to} className="flex flex-col items-start py-2 px-2.5" data-testid={`nav-platform-${t.to.replace("/", "")}`}>
                    <span className="text-sm font-medium text-foreground">{t.label}</span>
                    <span className="text-xs text-muted-foreground">{t.desc}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 px-2.5 py-2 text-[0.82rem] whitespace-nowrap rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 focus:outline-none" data-testid="nav-solutions-trigger">
              Solutions <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-navy-800 border-white/12 w-80 p-1.5">
              <p className="px-2.5 pt-2 pb-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Solutions</p>
              {SOLUTION_LINKS.map((t) => (
                <DropdownMenuItem key={t.to} asChild className="rounded-lg focus:bg-white/5 cursor-pointer">
                  <Link to={t.to} className="flex flex-col items-start py-2 px-2.5" data-testid={`nav-solution-${t.to.replace("/", "")}`}>
                    <span className="text-sm font-medium text-foreground">{t.label}</span>
                    <span className="text-xs text-muted-foreground">{t.desc}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 px-2.5 py-2 text-[0.82rem] whitespace-nowrap rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 focus:outline-none" data-testid="nav-products-trigger">
              Products <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-navy-800 border-white/12 w-80 p-1.5">
              <p className="px-2.5 pt-2 pb-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Products</p>
              {PRODUCT_LINKS.map((t) => {
                const Icon = productIcons[t.to];
                return (
                  <DropdownMenuItem key={t.to} asChild className="rounded-lg focus:bg-white/5 cursor-pointer">
                    <Link to={t.to} className="flex items-start gap-3 p-2.5" data-testid={`nav-product-${t.to.replace("/", "")}`}>
                      <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                        {Icon && <Icon className="h-5 w-5" />}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">{t.label}</span>
                        <span className="text-xs text-muted-foreground leading-snug">{t.desc}</span>
                      </span>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 px-2.5 py-2 text-[0.82rem] whitespace-nowrap rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 focus:outline-none">
              Tools <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-navy-800 border-white/12 w-64">
              {TOOL_LINKS.map((t) => (
                <DropdownMenuItem key={t.to} asChild className="focus:bg-white/5 cursor-pointer">
                  <Link to={t.to} className="flex flex-col items-start py-2">
                    <span className="text-sm text-foreground">{t.label}</span>
                    <span className="text-xs text-muted-foreground">{t.desc}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {NAV_LINKS.map((l) => {
            const active = pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`px-2.5 py-2 text-[0.82rem] whitespace-nowrap rounded-md transition-colors ${
                  active
                    ? "text-foreground bg-white/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
          >
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <Link to="/book-a-demo" className="inline-flex items-center gap-1.5 text-[0.82rem] font-medium text-foreground hover:text-primary transition-colors" data-testid="nav-book-walkthrough">
            Book a walkthrough <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <button
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle-mobile"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-foreground/5"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-white/5"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm bg-navy-900 border-white/10 p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
                <Logo size={32} />
              </div>
              <div className="flex flex-col p-4">
                <p className="px-3 pt-2 pb-1 text-xs uppercase tracking-wide text-muted-foreground/70">Platform</p>
                {PLATFORM_LINKS.map((t) => (
                  <SheetClose asChild key={t.to}>
                    <Link to={t.to} className="px-3 py-3 text-base rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5">{t.label}</Link>
                  </SheetClose>
                ))}
                <p className="px-3 pt-4 pb-1 text-xs uppercase tracking-wide text-muted-foreground/70">Solutions</p>
                {SOLUTION_LINKS.map((t) => (
                  <SheetClose asChild key={t.to}>
                    <Link to={t.to} className="px-3 py-3 text-base rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5">{t.label}</Link>
                  </SheetClose>
                ))}
                <p className="px-3 pt-4 pb-1 text-xs uppercase tracking-wide text-muted-foreground/70">Products</p>
                {PRODUCT_LINKS.map((t) => (
                  <SheetClose asChild key={t.to}>
                    <Link
                      to={t.to}
                      className="px-3 py-3 text-base rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5"
                    >
                      {t.label}
                    </Link>
                  </SheetClose>
                ))}
                <p className="px-3 pt-4 pb-1 text-xs uppercase tracking-wide text-muted-foreground/70">Tools</p>
                {TOOL_LINKS.map((t) => (
                  <SheetClose asChild key={t.to}>
                    <Link
                      to={t.to}
                      className="px-3 py-3 text-base rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5"
                    >
                      {t.label}
                    </Link>
                  </SheetClose>
                ))}
                <div className="mt-2 border-t border-white/10 pt-2">
                  {NAV_LINKS.map((l) => (
                    <SheetClose asChild key={l.to}>
                      <Link to={l.to} className="block px-3 py-3 text-base rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5">{l.label}</Link>
                    </SheetClose>
                  ))}
                </div>
                <SheetClose asChild>
                  <Button asChild className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/book-a-demo">Book a walkthrough</Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
